import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import { HistoryModal } from "@/components/history/HistoryModal";
import AntIcon from "@/components/icons/AntIcon";
import BackNavigationArrow from "@/components/views/BackNavigationArrow";
import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { appendSearchParamsToURL } from "@/utils/utils";
import { Button, Card, Col, Row, Segmented, Space, Tabs, TabsProps } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useGetAgent, useGetCommissionsSettings, useGetProfileCommissionsSettings } from "../../../common/api/queries";
import { rowGutter } from "../../../common/constants";
import { TopAgentType } from "../../../common/enums";
import { Permission } from "../../../common/security/enums";
import { EntityIdObject } from "../../../common/types";
import type { UUID } from "../../../typings/global";
import { useHasPermissions } from "../../auth/hooks";
import { BailAccountSettingsForm } from "../../commissions/bailaccount/components/forms/BailAccountSettingsForm";
import BailAccountMovementTableView from "../../commissions/bailaccount/components/views/BailAccountMovementTableView";
import { BailAccountSettingsView } from "../../commissions/bailaccount/components/views/BailAccountSettingsView";
import { DebtAccountMovementTable } from "../../commissions/debtaccount/components/tables/DebtAccountMovementTable";
import { CommissionsCategory } from "../../commissions/enums";
import { CommissionsOutputsAgentTable } from "../../commissions/outputs/components/tables/CommissionsOutputsAgentTable";
import { CommissionsSettingsRuleForm } from "../../commissions/settings/components/forms/CommissionsSettingsRuleForm";
import { CommissionsSettingsRuleHelperView } from "../../commissions/settings/components/views/CommissionsSettingsRuleHelperView";
import { CommissionsSettingsRuleTableView } from "../../commissions/settings/components/views/CommissionsSettingsRuleTableView";
import {
  CommissionsSettings,
  CommissionsSettingsAgentProfile,
  CommissionsSettingsRule,
} from "../../commissions/settings/types";
import { isCommissionsSettings } from "../../commissions/settings/utils";
import { SpecialCommissionsSettingsRuleForm } from "../../commissions/special/components/forms/SpecialCommissionsSettingsRuleForm";
import { SpecialCommissionsSettingsRuleTable } from "../../commissions/special/components/tables/SpecialCommissionsSettingsRuleTable";
import { SpecialCommissionsSettingsRule } from "../../commissions/special/types";
import { AgentCommissionsSummary } from "../components/AgentCommissionsSummary";
import { AgentImportantInfo } from "../components/AgentImportantInfo";
import { AgentTypeTag } from "../components/tags/AgentTypeTag";
import { AGENT_ROUTE_PATHS } from "../paths";
import { formatAgentAggregatedName, isAgentTipper } from "../utils";

const TAB = {
  OUTPUTS: "outputs",
  RULES: "rules",
  SPECIAL_RULES: "special-rules",
  BAIL_ACCOUNT: "bail-account",
  DEBT_ACCOUNT: "debt-account",
};

export const AgentCommissionsContainer = () => {
  const { t } = useTranslation();

  const { id } = useParams<EntityIdObject>() as { id: string };
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const urlTabKey = urlParams.get("tab");

  const [commissionsRuleFormOpen, setCommissionsRuleFormOpen] = useState(false);
  const [commissionsRuleToUpdate, setCommissionsRuleToUpdate] = useState<CommissionsSettingsRule>();
  const [specialCommissionsRuleFormOpen, setSpecialCommissionsRuleFormOpen] = useState(false);
  const [specialCommissionsRuleToUpdate, setSpecialCommissionsRuleToUpdate] =
    useState<SpecialCommissionsSettingsRule>();
  const [bailAccountSettingsFormOpen, setBailAccountSettingsFormOpen] = useState(false);
  const [historyModalIsOpen, setHistoryModalIsOpen] = useState(false);

  const hasCommissionsPermission = useHasPermissions(Permission.COMMISSIONS);
  const hasAgentCommissionsSettingsPermission = useHasPermissions(Permission.AGENT_COMMISSIONS_SETTINGS);
  const hasManageAgentCommissionsSettingsPermission = useHasPermissions(Permission.AGENT_COMMISSIONS_SETTINGS_MANAGE);

  const { data: agent, isError } = useGetAgent(id);
  const isTopAgentTypePlus = agent?.topAgentType === TopAgentType.PLUS;
  const isCurrentAgentTipper = !agent || isAgentTipper(agent);

  const [bailAccountCommissionsCategory, setBailAccountCommissionsCategory] = useState<CommissionsCategory>(
    isCurrentAgentTipper ? CommissionsCategory.PRIMARY_TIPPER : CommissionsCategory.PRIMARY_BROKER,
  );
  const [debtAccountCommissionsCategory, setDebtAccountCommissionsCategory] = useState<CommissionsCategory>(
    isCurrentAgentTipper ? CommissionsCategory.PRIMARY_TIPPER : CommissionsCategory.PRIMARY_BROKER,
  );

  const { data: agentCommissionsSettings } = useGetCommissionsSettings(
    agent?.id,
    !!agent?.id && hasAgentCommissionsSettingsPermission,
  );
  const { data: profileCommissionsSettings } = useGetProfileCommissionsSettings(
    !hasAgentCommissionsSettingsPermission ? agent?.id : undefined,
  );

  const commissionsSettings: CommissionsSettings | CommissionsSettingsAgentProfile | undefined =
    agentCommissionsSettings ?? profileCommissionsSettings;

  const resolveTabKey = () => {
    switch (urlTabKey) {
      case TAB.OUTPUTS:
      case TAB.BAIL_ACCOUNT:
      case TAB.DEBT_ACCOUNT:
        return urlTabKey;
      case TAB.RULES:
      case TAB.SPECIAL_RULES:
        return hasAgentCommissionsSettingsPermission ? urlTabKey : TAB.OUTPUTS;
      default:
        return TAB.OUTPUTS;
    }
  };

  const tabKey = resolveTabKey();

  const handleTabKeyChange = (key: string) => {
    navigate(appendSearchParamsToURL({ pageIndex: undefined, tab: key }), { replace: true });
  };

  const handleCommissionsSettingsRuleUpdateClick = (rule: CommissionsSettingsRule) => {
    setCommissionsRuleFormOpen(true);
    setCommissionsRuleToUpdate(rule);
  };

  const handleCommissionsSettingsRuleFormCancel = () => {
    setCommissionsRuleFormOpen(false);
    setCommissionsRuleToUpdate(undefined);
  };

  const handleSpecialCommissionsRuleUpdateClick = (rule: SpecialCommissionsSettingsRule) => {
    setSpecialCommissionsRuleFormOpen(true);
    setSpecialCommissionsRuleToUpdate(rule);
  };

  const handleSpecialCommissionsRuleFormCancel = () => {
    setSpecialCommissionsRuleFormOpen(false);
    setSpecialCommissionsRuleToUpdate(undefined);
  };

  const getItems = (hasAgentCommissionsSettingsPermission: boolean, agentId: UUID): TabsProps["items"] => {
    const items: TabsProps["items"] = [
      {
        key: TAB.OUTPUTS,
        icon: <AntIcon type="euro-circle" />,
        label: t("commissions.settings.sections.outputs"),
        children: (
          <CommissionsOutputsAgentTable agentId={agentId} showFullBatchNameWithLink={hasCommissionsPermission} />
        ),
      },
    ];

    if (hasAgentCommissionsSettingsPermission) {
      items.push({
        key: TAB.RULES,
        icon: <AntIcon type="settings" />,
        label: t("commissions.settings.sections.rules"),
        children: (
          <>
            <CommissionsSettingsRuleTableView
              rules={commissionsSettings && isCommissionsSettings(commissionsSettings) ? commissionsSettings.rules : []}
              agent={agent}
              showActions={hasManageAgentCommissionsSettingsPermission}
              onUpdateClick={handleCommissionsSettingsRuleUpdateClick}
            />

            {hasManageAgentCommissionsSettingsPermission && (
              <div className="margin-top-small">
                <Space>
                  <Button
                    type="primary"
                    size="small"
                    icon={<AntIcon type="plus" />}
                    onClick={() => setCommissionsRuleFormOpen(true)}
                  >
                    {t("commissions.settings.actions.addRule")}
                  </Button>
                  <CommissionsSettingsRuleHelperView />
                </Space>
              </div>
            )}
          </>
        ),
      });

      items.push({
        key: TAB.SPECIAL_RULES,
        icon: <AntIcon type="retweet" />,
        label: t("commissions.special.titles.page"),
        children: (
          <>
            <SpecialCommissionsSettingsRuleTable
              rules={
                commissionsSettings && isCommissionsSettings(commissionsSettings)
                  ? commissionsSettings.specialRules
                  : []
              }
              agentId={agentId}
              showActions={hasManageAgentCommissionsSettingsPermission}
              onUpdateClick={handleSpecialCommissionsRuleUpdateClick}
            />

            {hasManageAgentCommissionsSettingsPermission && (
              <div
                className={
                  commissionsSettings &&
                  isCommissionsSettings(commissionsSettings) &&
                  commissionsSettings.specialRules.length
                    ? "table-footer-margin"
                    : "margin-top-small"
                }
              >
                <Button
                  type="primary"
                  size="small"
                  icon={<AntIcon type="plus" />}
                  onClick={() => setSpecialCommissionsRuleFormOpen(true)}
                >
                  {t("commissions.special.actions.addRule")}
                </Button>
              </div>
            )}
          </>
        ),
      });
    }

    if (isTopAgentTypePlus) {
      items.push(
        {
          key: TAB.BAIL_ACCOUNT,
          icon: <AntIcon type="plus-square" />,
          label: `${t("commissions.bailAccount.titles.page")}`,
          children: (
            <Row gutter={rowGutter}>
              <Segmented
                size="large"
                className="margin-bottom-small"
                options={[
                  {
                    label: t("commissions.enums.commissionsCategory_PLUS.PRIMARY_TIPPER"),
                    value: CommissionsCategory.PRIMARY_TIPPER,
                  },
                  {
                    label: t("commissions.enums.commissionsCategory_PLUS.PRIMARY_BROKER"),
                    value: CommissionsCategory.PRIMARY_BROKER,
                  },
                ]}
                value={bailAccountCommissionsCategory}
                onChange={setBailAccountCommissionsCategory}
              />

              <Col span={24}>
                <BailAccountSettingsView
                  settings={commissionsSettings?.bailAccountSettings}
                  showActions={hasManageAgentCommissionsSettingsPermission}
                  noSettingsLabel={t("commissions.bailAccount.helpers.noSettings")}
                  onCreateUpdateClick={() => setBailAccountSettingsFormOpen(true)}
                  viewType="agent-commissions"
                  parentId={agentId}
                />
              </Col>

              <Col span={24}>
                <BailAccountMovementTableView
                  agentId={agentId}
                  category={bailAccountCommissionsCategory}
                  showFullBatchNameWithLink={hasCommissionsPermission}
                  showActions={hasManageAgentCommissionsSettingsPermission}
                  showCreateMovementAction={!!commissionsSettings?.bailAccountSettings}
                  showFilterSwitch
                />
              </Col>
            </Row>
          ),
        },
        {
          key: TAB.DEBT_ACCOUNT,
          icon: <AntIcon type="minus-square" />,
          label: `${t("commissions.debtAccount.titles.page")}`,
          children: (
            <>
              <Segmented
                size="large"
                className="margin-bottom-small"
                options={[
                  {
                    label: t("commissions.enums.commissionsCategory_PLUS.PRIMARY_TIPPER"),
                    value: CommissionsCategory.PRIMARY_TIPPER,
                  },
                  {
                    label: t("commissions.enums.commissionsCategory_PLUS.PRIMARY_BROKER"),
                    value: CommissionsCategory.PRIMARY_BROKER,
                  },
                  {
                    label: t("commissions.enums.commissionsCategory_PLUS.SECONDARY"),
                    value: CommissionsCategory.SECONDARY,
                  },
                ]}
                value={debtAccountCommissionsCategory}
                onChange={setDebtAccountCommissionsCategory}
              />

              <DebtAccountMovementTable
                agentId={agentId}
                category={debtAccountCommissionsCategory}
                showFullBatchNameWithLink={hasCommissionsPermission}
                showActions={hasManageAgentCommissionsSettingsPermission}
                showFilterSwitch
              />
            </>
          ),
        },
      );
    } else {
      items.push(
        {
          key: TAB.BAIL_ACCOUNT,
          icon: <AntIcon type="plus-square" />,
          label: t("commissions.bailAccount.titles.page"),
          children: (
            <Row gutter={rowGutter}>
              <Col span={24}>
                <BailAccountSettingsView
                  settings={commissionsSettings?.bailAccountSettings}
                  showActions={hasManageAgentCommissionsSettingsPermission}
                  noSettingsLabel={t("commissions.bailAccount.helpers.noSettings")}
                  onCreateUpdateClick={() => setBailAccountSettingsFormOpen(true)}
                  viewType="agent-commissions"
                  parentId={agentId}
                />
              </Col>

              <Col span={24}>
                <BailAccountMovementTableView
                  agentId={agentId}
                  category={CommissionsCategory.PRIMARY}
                  showFullBatchNameWithLink={hasCommissionsPermission}
                  showActions={hasManageAgentCommissionsSettingsPermission}
                  showCreateMovementAction={!!commissionsSettings?.bailAccountSettings}
                  showFilterSwitch
                />
              </Col>
            </Row>
          ),
        },
        {
          key: TAB.DEBT_ACCOUNT,
          icon: <AntIcon type="minus-square" />,
          label: t("commissions.debtAccount.titles.page"),
          children: (
            <DebtAccountMovementTable
              agentId={agentId}
              showFullBatchNameWithLink={hasCommissionsPermission}
              category={CommissionsCategory.PRIMARY}
              showActions={hasManageAgentCommissionsSettingsPermission}
              showFilterSwitch
            />
          ),
        },
      );
    }

    return items;
  };

  return (
    <DisplayWrapper itemLoaded={!!agent} isError={isError}>
      {agent && (
        <>
          <Card
            className="card-box"
            title={
              <>
                <BackNavigationArrow>
                  <h2>{`${t("commissions.settings.titles.page")} | ${formatAgentAggregatedName(agent)}`}</h2>
                </BackNavigationArrow>
                <AgentTypeTag style={{ margin: "6px 0 0 8px" }} type={agent.type} />
              </>
            }
            extra={
              <>
                {commissionsSettings && isCommissionsSettings(commissionsSettings) ? (
                  <Button icon={<AntIcon type="history" />} onClick={() => setHistoryModalIsOpen(true)}>
                    {t("commissions.settings.actions.showHistory")}
                  </Button>
                ) : undefined}

                <Link to={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: agent.id })}>
                  <SecondaryButton icon={<AntIcon type="user" />}>{t("agent.actions.goToAgent")}</SecondaryButton>
                </Link>
              </>
            }
          >
            <AgentImportantInfo agent={agent} />

            {commissionsSettings ? (
              <AgentCommissionsSummary agent={agent} commissionsSettings={commissionsSettings} />
            ) : undefined}

            <Tabs
              activeKey={tabKey}
              className="margin-top-large"
              destroyOnHidden
              onChange={handleTabKeyChange}
              items={getItems(hasAgentCommissionsSettingsPermission, agent.id)}
            />
          </Card>

          <CommissionsSettingsRuleForm
            open={commissionsRuleFormOpen}
            rule={commissionsRuleToUpdate}
            agent={agent}
            onFormCancel={handleCommissionsSettingsRuleFormCancel}
          />

          <SpecialCommissionsSettingsRuleForm
            open={specialCommissionsRuleFormOpen}
            rule={specialCommissionsRuleToUpdate}
            agent={agent}
            onFormCancel={handleSpecialCommissionsRuleFormCancel}
          />

          <BailAccountSettingsForm
            settings={commissionsSettings?.bailAccountSettings}
            open={bailAccountSettingsFormOpen}
            onFormCancel={() => setBailAccountSettingsFormOpen(false)}
            viewType="agent-commissions"
            parentId={agent.id}
          />

          {commissionsSettings && isCommissionsSettings(commissionsSettings) ? (
            <HistoryModal
              item={commissionsSettings}
              type="commissions-settings"
              isOpen={historyModalIsOpen}
              translationRootPath="commissions.settings.attrs"
              onCancel={() => setHistoryModalIsOpen(false)}
            />
          ) : undefined}
        </>
      )}
    </DisplayWrapper>
  );
};
